
import { useMemo, useCallback } from 'react';
import { useAttendeesData } from '@/hooks/useAttendeesData';
import { useAdminStats } from '@/hooks/useAdminStats';
import { Attendee } from '@/types/attendee';

/**
 * Hook for managing attendees data in the admin page
 * with optimized database access patterns to prevent blocking
 */
export const useAdminPageAttendees = () => {
  // Using pollingInterval of 15000 (15 seconds) to reduce database load
  // This prevents too frequent database accesses that could cause blocking
  const { 
    attendees, 
    refreshAttendees, 
    silentlyUpdateAttendee,
    lastRefreshTime,
    generateDemoData, 
    resetData,
    isLoading: attendeesLoading,
    usingSupabase: attendeesUsingSupabase,
    updateAttendee // Ensure we're getting this from useAttendeesData
  } = useAttendeesData(15000); // 15-second polling to reduce database pressure
  
  // Calculate stats - memoize to prevent re-renders
  const {
    totalRegistered,
    totalAttended,
    totalContribution,
    attendancePercentage,
    tShirtStats
  } = useAdminStats(attendees);
  
  // Add a check to detect empty attendee list with successful connection
  const hasEmptyAttendees = useMemo(() => {
    return attendees.length === 0 && !attendeesLoading && attendeesUsingSupabase;
  }, [attendees, attendeesLoading, attendeesUsingSupabase]);
  
  // Enhanced version of updateAttendee that ensures t_shirt_size is preserved
  const enhancedUpdateAttendee = useCallback(async (code: string, updates: any) => {
    // Find the current attendee data to get the t_shirt_size if needed
    const currentAttendee = attendees.find(a => a.code === code);
    
    // Ensure t_shirt_size is included in updates
    const enhancedUpdates = {
      ...updates,
      t_shirt_size: updates.t_shirt_size || (currentAttendee ? currentAttendee.t_shirt_size : undefined)
    };
    
    console.log(`Enhanced update for ${code} with t_shirt_size:`, enhancedUpdates.t_shirt_size);
    
    return await updateAttendee(code, enhancedUpdates);
  }, [attendees, updateAttendee]);
  
  // Optimized version of silentlyUpdateAttendee with debouncing logic
  const handleSilentUpdate = useCallback((updatedAttendee: Attendee) => {
    // Find existing attendee to preserve t_shirt_size if needed
    const existingAttendee = attendees.find(a => a.code === updatedAttendee.code);
    
    // Prevent unnecessary updates if the data hasn't changed
    if (existingAttendee && 
        existingAttendee.attended === updatedAttendee.attended &&
        existingAttendee.checkedInAt === updatedAttendee.checkedInAt) {
      return; // Skip update if nothing changed
    }
    
    // Always ensure t_shirt_size is preserved
    const attendeeWithTShirt = {
      ...updatedAttendee,
      t_shirt_size: updatedAttendee.t_shirt_size || (existingAttendee ? existingAttendee.t_shirt_size : undefined)
    };
    
    console.log(`Silent update for ${updatedAttendee.code} with preserved t_shirt_size:`, attendeeWithTShirt.t_shirt_size);
    
    silentlyUpdateAttendee(attendeeWithTShirt);
  }, [silentlyUpdateAttendee, attendees]);
  
  // Optimized refresh that prevents redundant calls within a timeframe
  const throttledRefresh = useCallback(async () => {
    // Don't refresh if we already did within the last 3 seconds
    const now = Date.now();
    const lastRefreshTimeMs = lastRefreshTime ? new Date(lastRefreshTime).getTime() : 0;
    if (now - lastRefreshTimeMs < 3000) {
      return attendees;
    }
    
    return await refreshAttendees();
  }, [refreshAttendees, lastRefreshTime, attendees]);
  
  return {
    attendees,
    refreshAttendees: throttledRefresh,
    silentlyUpdateAttendee: handleSilentUpdate,
    lastRefreshTime,
    generateDemoData,
    resetData,
    isLoading: attendeesLoading,
    usingSupabase: attendeesUsingSupabase,
    totalRegistered,
    totalAttended,
    totalContribution,
    attendancePercentage,
    tShirtStats,
    hasEmptyAttendees,
    updateAttendee: enhancedUpdateAttendee // Use our enhanced version
  };
};
